import { ModalConfig } from './config'

/**
 * Класс для управления слушателями событий модальных окон.
 */
export class ModalListeners {
  private onModalClickCallback: (_event: MouseEvent) => void
  private onModalKeyboardCallback: (_event: KeyboardEvent) => void
  private onTriggerClickCallback: (_event: MouseEvent) => void

  constructor(
    onModalClick: (_event: MouseEvent) => void,
    onModalKeyboard: (_event: KeyboardEvent) => void,
    onTriggerClick: (_event: MouseEvent) => void
  ) {
    this.onModalClickCallback = onModalClick
    this.onModalKeyboardCallback = onModalKeyboard
    this.onTriggerClickCallback = onTriggerClick
  }

  /**
   * Инициализирует триггеры для открытия модальных окон.
   */
  public initTriggers(): void {
    const triggers = document.querySelectorAll('[data-open-modal]')
    // eslint-disable-next-line no-console
    triggers.forEach((trigger) => {
      trigger.addEventListener(
        'click',
        this.onTriggerClickCallback as EventListener
      )
    })
  }

  /**
   * Добавляет триггер для открытия модального окна.
   * @param {HTMLElement} trigger - Элемент-триггер.
   */
  public addTrigger(trigger: HTMLElement): void {
    trigger.addEventListener(
      'click',
      this.onTriggerClickCallback as EventListener
    )
  }

  /**
   * Добавляет обработчики событий для модального окна.
   * @param {HTMLElement} modal - Элемент модального окна.
   * @param {boolean} [noKeydown=false] - Если true, не добавляет обработчик клавиатуры.
   */
  public addModalListeners(
    modal: HTMLElement,
    noKeydown: boolean = false
  ): void {
    modal.addEventListener('click', this.onModalClickCallback)

    if (!noKeydown) {
      document.addEventListener('keydown', this.onModalKeyboardCallback)
    }
  }

  /**
   * Удаляет обработчики событий с модального окна.
   * @param {HTMLElement} modal - Элемент модального окна.
   * @param {boolean} [noKeydown=false] - Если true, не удаляет обработчик клавиатуры.
   */
  public removeModalListeners(
    modal: HTMLElement,
    noKeydown: boolean = false
  ): void {
    modal.removeEventListener('click', this.onModalClickCallback)

    if (!noKeydown) {
      document.removeEventListener('keydown', this.onModalKeyboardCallback)
    }
  }

  /**
   * Создает обработчик клика по триггеру открытия модального окна.
   * @param {Function} openModalFn - Функция открытия модального окна.
   * @return {Function} Обработчик клика.
   */
  public static createTriggerClickHandler(
    openModalFn: (_modalName: string, _event: MouseEvent) => void
  ): (_event: MouseEvent) => void {
    return (_event: MouseEvent): void => {
      _event.preventDefault()

      const target = (_event.target as HTMLElement).closest(
        '[data-open-modal]'
      ) as HTMLElement
      const modalName = target.dataset.openModal

      if (modalName) {
        openModalFn(modalName, _event)
      }
    }
  }

  /**
   * Создает обработчик клика внутри модального окна.
   * @param {Function} getCurrentModalFn - Функция получения текущего модального окна.
   * @param {Function} closeModalFn - Функция закрытия модального окна.
   * @return {Function} Обработчик клика.
   */
  public static createModalClickHandler(
    getCurrentModalFn: () => string | null,
    closeModalFn: (_modalName: string, _isModalBack: boolean) => void
  ): (_event: MouseEvent) => void {
    return (_event: MouseEvent): void => {
      const closeTrigger = (_event.target as HTMLElement).closest(
        '[data-close-modal]'
      ) as HTMLElement

      if (!closeTrigger) {
        return
      }

      const currentModalName = getCurrentModalFn()
      if (!currentModalName) {
        return
      }

      const config = ModalConfig.get(currentModalName)

      closeModalFn(
        currentModalName,
        closeTrigger.dataset.closeModal === 'back' ||
          !!(config?.openPrevious && !config?.openOver)
      )
    }
  }

  /**
   * Создает обработчик событий клавиатуры для модального окна.
   * @param {Function} getCurrentModalFn - Функция получения текущего модального окна.
   * @param {Function} closeModalFn - Функция закрытия модального окна.
   * @return {Function} Обработчик событий клавиатуры.
   */
  public static createModalKeyboardHandler(
    getCurrentModalFn: () => string | null,
    closeModalFn: (_modalName: string, _isModalBack: boolean) => void
  ): (_event: KeyboardEvent) => void {
    return (_event: KeyboardEvent): void => {
      const isEscKey = _event.key === 'Escape' || _event.key === 'Esc'

      if (!isEscKey) {
        return
      }

      const currentModalName = getCurrentModalFn()
      if (!currentModalName) {
        return
      }

      const config = ModalConfig.get(currentModalName)

      _event.preventDefault()
      closeModalFn(
        currentModalName,
        config?.openPrevious || config?.openOver || false
      )
    }
  }
}
